import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, Save, ExternalLink } from "lucide-react";

function Row({ item, onSaved, onDeleted }) {
  const [d, setD] = useState(item);
  const isNew = !item.cid;
  const set = (k, v) => setD({ ...d, [k]: v });
  const save = async () => {
    if (!d.label_en?.trim()) return toast.error("Label (EN) required");
    try {
      const res = isNew ? await api.post("/admin/connect", d) : await api.put(`/admin/connect/${item.cid}`, d);
      toast.success("Saved"); onSaved(res.data, isNew);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async () => { if (isNew) return onDeleted(); if (!window.confirm("Remove this link?")) return; await api.delete(`/admin/connect/${item.cid}`); toast.success("Removed"); onDeleted(); };
  return (
    <div data-testid={isNew ? "connect-new-row" : `connect-row-${d.key}`} className="grid sm:grid-cols-12 gap-2 items-center border border-white/10 rounded-sm p-3 bg-white/5">
      <span className="sm:col-span-1 text-xs uppercase text-paper/40">{d.kind}</span>
      <input value={d.label_en || ""} onChange={(e) => set("label_en", e.target.value)} placeholder="Label EN" className="sm:col-span-2 bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm" />
      <input value={d.label_fr || ""} onChange={(e) => set("label_fr", e.target.value)} placeholder="Label FR" className="sm:col-span-2 bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm" />
      <input data-testid="connect-url" value={d.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="URL (https://… or /route)" className="sm:col-span-4 bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm" />
      <label className="sm:col-span-1 flex items-center gap-1 text-xs"><input type="checkbox" checked={!!d.enabled} onChange={(e) => set("enabled", e.target.checked)} /> On</label>
      <div className="sm:col-span-2 flex gap-1 justify-end">
        <button data-testid={isNew ? "connect-create" : `connect-save-${d.key}`} onClick={save} className="bg-heraldic text-white px-2.5 py-1.5 rounded-sm"><Save className="h-3.5 w-3.5" /></button>
        <button onClick={del} className="border border-white/15 text-paper/60 px-2.5 py-1.5 rounded-sm hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export default function AdminConnect() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [adding, setAdding] = useState(null);
  const load = () => { api.get("/admin/connect").then((r) => setItems(r.data)); api.get("/admin/connect/stats").then((r) => setStats(r.data)); };
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl" data-testid="admin-connect-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl">Social Conversion Hub</h1>
            <p className="text-paper/50 text-sm mt-1">Manage the links on your public bio-link page. Share <a href="/connect" target="_blank" rel="noreferrer" className="text-heraldic inline-flex items-center gap-1">simo.university/connect <ExternalLink className="h-3 w-3" /></a> everywhere. Add <code className="text-paper/70">?ref=tiktok</code> to track sources.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="connect-add-cta" onClick={() => setAdding({ kind: "cta", label_en: "", label_fr: "", url: "", enabled: true, order: 99 })} className="inline-flex items-center gap-1.5 bg-heraldic text-white px-3 py-2 rounded-sm text-sm"><Plus className="h-4 w-4" /> CTA</button>
            <button data-testid="connect-add-social" onClick={() => setAdding({ kind: "social", label_en: "", label_fr: "", url: "", icon: "", enabled: true, order: 99 })} className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 rounded-sm text-sm"><Plus className="h-4 w-4" /> Social</button>
          </div>
        </div>

        {stats && (
          <div className="mt-5 grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            <div className="bg-[#0B0D12] p-4"><div className="font-serif text-3xl text-heraldic">{stats.total}</div><div className="text-xs text-paper/50">Total events</div></div>
            <div className="bg-[#0B0D12] p-4"><div className="text-xs text-paper/50 mb-1">Top sources</div>{Object.entries(stats.by_ref).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => <div key={k} className="text-sm flex justify-between"><span className="capitalize">{k}</span><span className="text-paper/50">{v}</span></div>)}</div>
            <div className="bg-[#0B0D12] p-4"><div className="text-xs text-paper/50 mb-1">Top CTAs</div>{Object.entries(stats.by_cta).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([k, v]) => <div key={k} className="text-sm flex justify-between"><span>{k}</span><span className="text-paper/50">{v}</span></div>)}</div>
          </div>
        )}

        <div className="mt-6 space-y-2">
          {adding && <Row item={adding} onSaved={() => { setAdding(null); load(); }} onDeleted={() => setAdding(null)} />}
          {items === null && <p className="text-paper/40">Loading…</p>}
          {items && items.map((it) => <Row key={it.cid} item={it} onSaved={() => load()} onDeleted={() => load()} />)}
        </div>
      </div>
    </AdminLayout>
  );
}
